import React, { type ReactNode, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { borderRadius } from '../../../theme/theme';
import { useAuthStore } from '../../../shared/stores/useAuthStore';
import { requestHostWindowMode } from '../../../shared/lib/utils/windowSizing';
import { isGrpcRuntimeAvailable } from '../../../shared/api/grpc/grpcRuntime';
import { normalizeMachineFingerprint } from '../../../shared/lib/machineFingerprint';
import LoginAuthenticationPanel from './LoginAuthenticationPanel';
import LoginBrandPanel from './LoginBrandPanel';

export default function LoginPage() {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [activationKey, setActivationKey] = useState('');
  const {
    login,
    register,
    initializeFingerprint,
    loading,
    error,
    clearError,
    fingerprintLoading,
    machineFingerprint,
    fingerprintError,
  } = useAuthStore();
  const runtimeAvailable = isGrpcRuntimeAvailable();
  const verifiedFingerprint = !fingerprintLoading && !fingerprintError
    ? normalizeMachineFingerprint(machineFingerprint)
    : null;
  const runtimeSubmitBlocked = runtimeAvailable && (fingerprintLoading || !verifiedFingerprint);

  useEffect(() => {
    initializeFingerprint();
    requestHostWindowMode('authentication');
    return () => requestHostWindowMode('workspace');
  }, [initializeFingerprint]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (loading || runtimeSubmitBlocked) return;
    clearError();
    if (mode === 'register') {
      await register({
        username: username.trim(),
        password,
        email: email.trim(),
        activationKey: activationKey.trim(),
      });
      return;
    }
    await login(username.trim(), password);
  };

  const toggleMode = () => {
    setMode((currentMode) => (currentMode === 'login' ? 'register' : 'login'));
    setShowPassword(false);
    clearError();
  };

  return (
    <LoginPageShell>
      <LoginAuthenticationPanel
        mode={mode}
        username={username}
        password={password}
        email={email}
        activationKey={activationKey}
        showPassword={showPassword}
        loading={loading}
        error={error}
        runtimeAvailable={runtimeAvailable}
        runtimeSubmitBlocked={runtimeSubmitBlocked}
        fingerprintLoading={fingerprintLoading}
        verifiedFingerprint={verifiedFingerprint}
        fingerprintError={fingerprintError}
        onSubmit={handleSubmit}
        onToggleMode={toggleMode}
        setUsername={setUsername}
        setPassword={setPassword}
        setEmail={setEmail}
        setActivationKey={setActivationKey}
        setShowPassword={setShowPassword}
      />
    </LoginPageShell>
  );
}

function LoginPageShell({ children }: { children: ReactNode }) {
  const theme = useTheme();

  return (
    <Box
      component="main"
      aria-label="PipeEX runtime authentication"
      sx={{
        position: 'relative',
        width: '100%',
        height: '100vh',
        minHeight: 0,
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        p: { xs: 0, md: 3 },
        backgroundColor: '#0d0f12',
        backgroundImage: `radial-gradient(circle at 15% 18%, ${alpha(theme.palette.primary.main, 0.1)} 0%, transparent 34%), radial-gradient(circle at 88% 82%, ${alpha(theme.palette.primary.dark, 0.07)} 0%, transparent 30%)`,
        '@media (max-height: 620px)': { p: { xs: 0, md: 2 } },
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          opacity: 0.08,
          pointerEvents: 'none',
          backgroundImage: `linear-gradient(${alpha(theme.palette.common.white, 0.2)} 1px, transparent 1px), linear-gradient(90deg, ${alpha(theme.palette.common.white, 0.2)} 1px, transparent 1px)`,
          backgroundSize: '64px 64px',
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          width: '100%',
          maxWidth: 1180,
          height: '100%',
          maxHeight: 680,
          minHeight: 0,
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(390px, 0.92fr) minmax(440px, 1.08fr)' },
          overflow: 'hidden',
          border: { xs: 0, md: `1px solid ${alpha(theme.palette.common.white, 0.12)}` },
          borderRadius: { xs: 0, md: `${borderRadius.md + 6}px` },
          backgroundColor: '#131519',
          boxShadow: { xs: 'none', md: `0 28px 80px ${alpha(theme.palette.common.black, 0.42)}` },
        }}
      >
        <LoginBrandPanel />
        {children}
      </Box>
    </Box>
  );
}
